import { effect, Injectable } from '@angular/core';
import { StateSignal } from '../../../../@harmony/angular';
import { AlbumItem, DiagnosticsState } from './state/diagnostics.state';
import {
  initError,
  initLoading,
  initReady,
  initStopped,
  queueAll,
  updateAlbumItem,
  setIsProcessingQueue,
} from './state/diagnostics.reducers';
import { downloadString, getFlavorName, getLogger } from '../../../core/utils';
import { GetTeamAlbumsInteractor } from '../../../../features/albums/domain/interactors/get-team-albums.interactor';
import { FeaturesService } from '../../shared/services/features.service';
import { TroubleshootAlbumInteractor } from '../../../../features/albums/domain/interactors/troubleshoot-album.interactor';
import { DialogService } from '../../shared/services/dialog.service';

@Injectable({ providedIn: 'root' })
export class DiagnosticsService {
  private readonly logger = getLogger('DiagnosticsService');

  public readonly state: StateSignal<DiagnosticsState> = new StateSignal(initStopped());

  constructor(
    private readonly dialog: DialogService,
    private readonly featuresSvc: FeaturesService,
    private readonly getTeamAlbums: GetTeamAlbumsInteractor,
    private readonly troubleshootAlbum: TroubleshootAlbumInteractor,
  ) {}

  public init(): void {
    effect(() => {
      const ctx = this.featuresSvc.context();

      if (this.state.$value.$type === 'stopped' && ctx.team) {
        this.logger.info('Team changed, starting diagnostics service');
        this.start(ctx.team.id);
      } else if (this.state.$value.$type !== 'stopped' && !ctx.team) {
        this.logger.info('Team changed, stopping diagnostics service');
        this.stop();
      }
    });
  }

  private async start(teamId: string): Promise<void> {
    this.state.set(initLoading());

    try {
      const albums = await this.getTeamAlbums.execute(teamId);

      this.state.set(initReady(albums));

      // TODO: Eventually we want to "auto-start" diagnostics, for now disable it
      // this.queueAll();
    } catch (err) {
      this.logger.error('There was an issue while initializing the diagnostics service', err);
      this.state.set(initError(err));
    } finally {
    }
  }

  private stop(): void {
    this.state.set(initStopped());
  }

  private async processQueue(): Promise<void> {
    // If we're not in `ready` state, or it's already being processed, we can't process the queue
    if (!this.state.isType('ready') || this.state.$value.isProcessingQueue) {
      return;
    }

    const itemDone = { isProcessing: false, isQueued: false } as const;

    this.state.set(setIsProcessingQueue(true));
    this.logger.info('Starting to process the diagnostics queue');

    while (this.state.$value.albums.some((a) => a.isQueued)) {
      // SAFETY: `randomAlbum` will always be defined because we already checked
      // in the `while` statement that there is `some` queued value
      const queuedAlbums = this.state.$value.albums.filter((a) => a.isQueued);
      const randomAlbum = queuedAlbums[Math.floor(Math.random() * queuedAlbums.length)];

      this.logger.info(`Processing album: ${randomAlbum.data.id}`);
      this.state.set(updateAlbumItem(randomAlbum.data.id, { isProcessing: true }));

      try {
        const result = await this.troubleshootAlbum.execute(randomAlbum.data.id);
        this.state.set(updateAlbumItem(randomAlbum.data.id, { result, ...itemDone }));
      } catch (err) {
        // TODO: Give feedback to the user?
        this.state.set(updateAlbumItem(randomAlbum.data.id, { ...itemDone }));
      }

      // SAFETY: The state might have mutated while we were waiting, check to
      // ensure that `state` is still of type `ready`
      if (!this.state.isType('ready')) {
        return;
      }
    }

    // SAFETY: The last `if` in the while loop already checks that `state` is
    // still of `ready` type
    this.state.set(setIsProcessingQueue(false));
    this.logger.info('Finished processing the diagnostics queue');
  }

  public downloadReport(): void {
    if (!this.state.isType('ready')) {
      return;
    }

    try {
      const filename = `${getFlavorName()}-diagnostics-report.json`.toLowerCase();
      const report = JSON.stringify(
        this.state.$value.albums
          .filter((album): album is Required<AlbumItem> => !!album.result)
          .map((album) => ({
            album: album.data,
            result: album.result,
          })),
        null,
        2,
      );

      downloadString(report, filename);
    } catch (err) {
      console.error(err);
      this.dialog.showErrorAlert(err, 'diagnostics.downloadReportError');
    }
  }

  public queue(albumId: string): void {
    if (!this.state.isType('ready')) {
      return;
    }

    this.state.set(
      updateAlbumItem(albumId, { isProcessing: false, isQueued: true, result: undefined }),
    );
    this.processQueue();
  }

  public queueAll(): void {
    if (!this.state.isType('ready')) {
      return;
    }

    this.state.set(queueAll());
    this.processQueue();
  }
}
